import React, { useMemo, useState } from "react";
import { BiInfoCircle } from "react-icons/bi";
import { HiOutlineCalendar } from "react-icons/hi";
import { Assort } from "../../../models/Assort";
import "./style.scss";
import "../../../../style/block.scss";
import "../../../../style/feature.scss";
import "../../../../style/hovers.scss";
import { TicketAccess } from "../../../models/TicketAccess";
import "../../../../style/buttons.scss";
import getId from "../../../../utils/getId";
import { BasketItem } from "../../../models/BasketItem";
import { useTranslation } from "react-i18next";
import { QntStateItem } from "../../../models/QntStateItem";
import EAButton from "../../shared/Buttons/EAButton";
import PassModal from "../../Passes/SinglePass/PassModal";
import usePasses from "../../Passes/hooks/usePasses";
import useBasketManager from "../../FinalizePage/hooks/useBasketManager";
import useParentMessage from "../../../../utils/hooks/useParentMessage";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { State } from "../../../store";
import { FINALIZE_PAGE } from "../../../routes";
import EAModal from "../../shared/Modal/EAModal";
import { stripHtml } from "../../../../utils/stripHtml";
import { getMonthlyPassRate, getSavingsPercent } from "./passMonthlyRate";

interface IProps {
  ticketLimit?: number;
  item: Assort;
  exercise?: TicketAccess;
  preBasket: BasketItem[];
  addItemToBasket: (assort: Assort, exercise?: TicketAccess) => void;
  removeItemFromBasket: (assort: Assort, exercise?: TicketAccess) => void;
  qntState?: QntStateItem[];
  /** Cena karnetu na ok. 1 miesiąc z tej samej listy. */
  monthPassPrice?: number | null;
  /** Stawka miesięczna i oszczędność. Wymaga jawnego włączenia w module sprzedaży. */
  showMonthlyComparison?: boolean;
}

const formatAmount = (val: number): string => {
  return Number.isInteger(val) ? val.toString() : val.toFixed(2);
};

const PassLineSummary: React.FC<IProps> = ({
  item,
  exercise,
  preBasket,
  qntState,
  monthPassPrice = null,
  showMonthlyComparison = false,
}) => {
  const { t } = useTranslation();

  const getQnt = (value: Assort): number => {
    if (qntState && qntState.length > 0 && exercise) {
      const foundQnt = qntState.find(
        (entry) => entry.assortIds === getId(value) && entry.exerciseIds === exercise.exerciseIds,
      );
      return foundQnt ? foundQnt.count : 0;
    }
    const itemBasket = preBasket.find((entry) => entry.assortIds === getId(value));
    return itemBasket ? itemBasket.qnt : 0;
  };

  const [open, setOpen] = useState(false);
  const isDiscount = item.price !== item.listPrice && item.listPrice > item.price;
  const [openPass, setOpenPass] = useState(false);
  const { selectedDate, setDateBetween } = usePasses();
  const { handleAddBasketLimited } = useBasketManager();
  const { scrollToTop } = useParentMessage();
  const saleConfiguration = useSelector((state: State) => state.config.configuration);
  const navigate = useNavigate();
  const [addError, setAddError] = useState("");

  const handleClose = () => {
    setOpenPass(false);
  };

  const itemName = item.displayWebName || item.name;
  const descriptionPlain = useMemo(() => stripHtml(item.description || ""), [item.description]);
  const hasDescription = descriptionPlain.length > 0;
  const monthlyRate = showMonthlyComparison ? getMonthlyPassRate(item.price, item.dayValidate) : null;
  const savingsPercent =
    showMonthlyComparison && monthlyRate !== null && !isDiscount && monthPassPrice != null
      ? getSavingsPercent(monthlyRate, monthPassPrice)
      : null;

  return (
    <>
      <div
        className={[
          "ticket-line-summary",
          "ticket-line",
          "ticket-line--pass",
          "flex-between-center",
          getQnt(item) > 0 ? "ticket-line-active" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="ticket-line-left">
          <div className="ticket-line-name-block">
            {hasDescription ? (
              <b className="ticket-line-name" title={itemName}>
                {itemName}
              </b>
            ) : (
              <button
                type="button"
                className="ticket-line-name ticket-line-name-button"
                title={itemName}
                onClick={() => setOpen(true)}
              >
                {itemName}
              </button>
            )}

            {hasDescription && (
              <button type="button" className="ticket-line-description-button" onClick={() => setOpen(true)}>
                <span className="ticket-line-description">{descriptionPlain}</span>
                <BiInfoCircle className="ticket-line-description-more" aria-hidden="true" />
              </button>
            )}
          </div>
        </div>

        <div className="ticket-line-right flex-end-center">
          <div className="price-container">
            <div className="price-row">
              {isDiscount && (
                <span className="price price-through">
                  {formatAmount(item.listPrice)} {t("zl")}
                </span>
              )}
              <b className="price">
                {formatAmount(item.price)} {t("zl")}
              </b>
            </div>
            {monthlyRate !== null && (
              <span className="price-caption">
                <span>
                  {formatAmount(monthlyRate)} {t("zl")} {t("za_miesiac")}
                </span>
                {savingsPercent !== null && (
                  <span className="price-caption-savings">{t("oszczedzasz_procent", { percent: savingsPercent })}</span>
                )}
              </span>
            )}
          </div>

          <div className="qnt flex-end-center">
            <EAButton
              title={t("wybierz_date")}
              endIcon={<HiOutlineCalendar size={18} aria-hidden="true" />}
              onClick={() => setOpenPass(true)}
            />
          </div>
        </div>
      </div>

      <EAModal
        isOpen={open}
        title={""}
        onClose={() => setOpen(false)}
        cancelButtonLabel={t("przeczytalem")}
        hideAcceptButton
      >
        <p className="ticket-line-modal-name">{itemName}</p>
        {item.description && item.description.length > 1 ? (
          <div className="ticket-line-modal-description" dangerouslySetInnerHTML={{ __html: item.description }} />
        ) : (
          <p>{t("brak_opisu")}</p>
        )}
      </EAModal>

      <PassModal
        isOpen={openPass}
        onClose={handleClose}
        pass={item}
        selectedDate={selectedDate}
        setDateBetween={setDateBetween}
        errorMessage={addError}
        onAddToBasket={async () => {
          setAddError("");
          const result = await handleAddBasketLimited(item, 1, selectedDate);
          if (!result?.ok) {
            setAddError(result?.error ?? "");
            return;
          }
          handleClose();
          scrollToTop();
          if (saleConfiguration.popupAfterAddTicket === false) {
            setTimeout(() => {
              navigate(FINALIZE_PAGE);
            }, 100);
          }
        }}
      />
    </>
  );
};

export default PassLineSummary;
